import { supabase } from './supabase.ts';
import { Bill, BillStatus, BillFormData } from '../types/bill.ts';

export const billService = {
  async fetchBills() {
    const { data, error } = await supabase
      .from('bills')
      .select(`
        *,
        tenant:tenants (
          id,
          full_name
        ),
        property:properties (
          id,
          name
        )
      `)
      .order('due_date', { ascending: false })  // Primary sort by due date
      .order('created_at', { ascending: false }); // Secondary sort by creation time

    if (error) throw error;

    // Calculate dueAmount for each bill
    const billsWithDueAmount = data.map(bill => {
      const dueAmount = bill.amount - (bill.paid_amount || 0);
      return {
        ...bill,
        dueAmount: Math.max(0, dueAmount) // Ensure dueAmount is not negative
      };
    });

    return billsWithDueAmount;
  },

  async getBillById(id: string) {
    const { data, error } = await supabase
      .from('bills')
      .select(`
        *,
        tenant:tenants(id, full_name),
        property:properties(id, name)
      `)
      .eq('id', id)
      .single();

    if (error) {
      throw new Error(`Error fetching bill: ${error.message}`);
    }

    return data as Bill;
  },

  async createBill(billData: BillFormData) {
    // If bill is marked as Paid, set paid_amount to full amount
    const billToCreate = {
      ...billData,
      paid_amount: billData.status === 'Paid' ? billData.amount : 0
    };

    const { data, error } = await supabase
      .from('bills')
      .insert([billToCreate])
      .select()
      .single();

    if (error) {
      throw new Error(`Error creating bill: ${error.message}`);
    }

    return data as Bill;
  },

  async updateBillStatus(id: string, status: BillStatus) {
    const updates = {
      status,
      ...(status === 'Paid' ? { payment_date: new Date().toISOString() } : {})
    };

    const { data, error } = await supabase
      .from('bills')
      .update(updates)
      .eq('id', id)
      .select(`
        *,
        tenant:tenants(id, full_name),
        property:properties(id, name)
      `)
      .single();

    if (error) {
      throw new Error(`Error updating bill status: ${error.message}`);
    }

    return data as Bill;
  },

  async deleteBill(id: string) {
    try {
      // First delete related payments
      const { error: paymentsError } = await supabase
        .from('payments')
        .delete()
        .eq('bill_id', id);

      if (paymentsError) {
        throw new Error(`Error deleting related payments: ${paymentsError.message}`);
      }

      // Then delete the bill
      const { error: billError } = await supabase
        .from('bills')
        .delete()
        .eq('id', id);

      if (billError) {
        throw new Error(`Error deleting bill: ${billError.message}`);
      }
    } catch (err) {
      throw new Error(`Error in delete operation: ${err.message}`);
    }
  },
};
